
// Importar el cliente de Supabase
import { supabase } from "@/integrations/supabase/client";
import { Ad } from "@/models/ad";

interface FetchAdsOptions {
  filterByName?: string;
  filterByDate?: string;
  filterBySellerId?: string;
  includeExpired?: boolean;
  excludeExpired?: boolean;
  includeHidden?: boolean;
}

export async function fetchAds(options: FetchAdsOptions = {}): Promise<Ad[]> {
  try {
    console.log("Fetching ads with options:", options);
    
    let query = supabase
      .from("ads")
      .select("*");

    // Aplicar filtros
    if (options.filterByName) {
      query = query.ilike("client_name", `%${options.filterByName}%`);
    }

    if (options.filterByDate) {
      // Filtrar por fecha (anuncios activos en esa fecha)
      query = query.lte("start_date", options.filterByDate)
        .gte("end_date", options.filterByDate);
    }

    if (options.filterBySellerId) {
      query = query.eq("seller_id", options.filterBySellerId);
    }

    // Ordenar por fecha de fin descendente
    query = query.order("end_date", { ascending: false });

    // Ejecutar la consulta
    const { data: adsData, error } = await query;

    if (error) {
      console.error("Error fetching ads:", error);
      throw new Error(`Error al obtener anuncios: ${error.message}`);
    }

    console.log(`Fetched ${adsData?.length || 0} ads from database with options:`, options);

    // Transformar los datos
    const ads: Ad[] = adsData?.map(ad => {
      // Asegurarse de que screens_json sea un array válido
      let screens = [];
      try {
        screens = typeof ad.screens_json === 'string' 
          ? JSON.parse(ad.screens_json)
          : Array.isArray(ad.screens_json) 
            ? ad.screens_json 
            : [{
                screenId: ad.screen_id,
                screenName: 'Pantalla Principal'
              }];
      } catch (e) {
        console.error('Error parsing screens_json:', e);
        screens = [{
          screenId: ad.screen_id,
          screenName: 'Pantalla Principal'
        }];
      }

      const transformedAd = {
        id: ad.id,
        clientName: ad.client_name,
        screens: screens,
        duration: ad.duration || 0,
        startDate: ad.start_date,
        endDate: ad.end_date,
        amount: ad.amount || 0,
        sellerId: ad.seller_id,
        sellerName: ad.seller_name || "Desconocido",
        sellerEmail: ad.seller_email || '',
        type: ad.type || "image",
        adType: ad.ad_type || "sale",
        materialUrl: ad.material_url || '',
        isHidden: ad.is_hidden || false,
        isExpired: false, // Se establecerá después
        version: ad.version || 1 // Added missing version property
      };

      // Log para depuración
      console.log('Anuncio transformado:', {
        id: transformedAd.id,
        cliente: transformedAd.clientName,
        pantallas: transformedAd.screens
      });

      return transformedAd;
    }) || [];

    // Obtener la fecha actual y normalizarla a medianoche
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filtrar anuncios según las opciones
    const filteredAds = ads.filter(ad => {
      // Convertir las fechas de string a objetos Date y asegurar que se interpreten como medianoche
      const endDate = new Date(ad.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      // Un anuncio está vencido si su fecha de fin es MENOR O IGUAL que la fecha actual
      const isExpired = endDate <= today;
      ad.isExpired = isExpired;

      // Log detallado para depuración
      console.log(`Procesando anuncio: ${ad.clientName}`, {
        fechaFin: ad.endDate,
        fechaFinObj: endDate.toISOString(),
        fechaActual: today.toISOString(),
        vencido: isExpired,
        oculto: ad.isHidden,
        comparacion: {
          endDateTimestamp: endDate.getTime(),
          todayTimestamp: today.getTime(),
          diferenciaDias: Math.floor((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        }
      });

      if (options.excludeExpired) {
        return !isExpired && !ad.isHidden;
      }
      
      if (options.includeExpired) {
        return isExpired;
      }

      return true;
    });

    // Log final de anuncios filtrados
    console.log("Anuncios filtrados:", {
      total: filteredAds.length,
      anuncios: filteredAds.map(ad => ({
        cliente: ad.clientName,
        fechaFin: ad.endDate,
        vencido: ad.isExpired,
        oculto: ad.isHidden
      }))
    });

    return filteredAds;
  } catch (error) {
    console.error("Error in fetchAds:", error);
    throw error;
  }
}

// Función para verificar el estado de los anuncios vencidos
export async function checkExpiredAdsStatus() {
  try {
    console.log("Verificando estado de anuncios vencidos...");
    
    // TEMPORALMENTE COMENTADO - La función check_expired_ads no existe en Supabase
    /*
    const { data, error } = await supabase.rpc('check_expired_ads');
    
    if (error) {
      console.error("Error al verificar anuncios vencidos:", error);
      throw new Error(`Error al verificar anuncios vencidos: ${error.message}`);
    }
    
    console.log("Estado de anuncios vencidos:", {
      totalExpired: data.total_expired,
      totalHidden: data.total_hidden,
      totalVisible: data.total_visible,
      details: data.expired_details
    });
    
    return data;
    */
    
    // Devolver datos dummy por ahora
    console.log("checkExpiredAdsStatus deshabilitado temporalmente");
    return {
      total_expired: 0,
      total_hidden: 0,
      total_visible: 0,
      expired_details: []
    };
  } catch (error) {
    console.error("Error en checkExpiredAdsStatus:", error);
    throw error;
  }
}
